import React, { useState, useRef, useEffect } from 'react';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { useAuth0 } from '@auth0/auth0-react';
import AttachmentIcon from '../../../Images/attachment-icon.svg';
import EmojiIcon from '../../../Images/emoji-icon.svg';
import { 
  Message, 
  IconWrapper, 
  Input, 
  LeftSection, 
  RightSection,
  FilePreview,
  RemoveFileButton,
  RecordingIndicator,
  RecordingTimer,
  TranscriptionLoadingIndicator
} from '../styled/MessageInput';
import { readFileAsBase64 } from '../utils/fileUtils';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useToast } from '../../../Context/ToastContext';

const MessageInput = ({ 
  message, 
  setMessage, 
  selectedFile, 
  setSelectedFile, 
  handleSendMessage, 
  loadingMessage
}) => {
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const uploadRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  
  // Speech-to-text state management
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState('');
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordingMimeType, setRecordingMimeType] = useState('audio/webm');
  
  // Required refs for speech-to-text
  const timerRef = useRef(null);
  
  // Get authenticated axios instance and user
  const authenticatedAxios = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { user } = useAuth0();

  /**
   * Show file picker
   */
  const uploadDoc = () => {
    if (uploadRef.current) {
      uploadRef.current.click();
    }
  };

  /**
   * Handle text input
   */
  const handleInputChange = (event) => {
    setMessage(event.target.value);
  };

  /**
   * Toggle emoji picker visibility
   */
  const toggleEmojiPicker = () => {
    setEmojiPickerVisible((prev) => !prev);
  };

  /**
   * Add emoji to message input
   */
  const emojiSelected = (event) => {
    setMessage((prev) => prev + event.native);
  };

  /**
   * Handle file selection
   */
  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (!files || !files.length) return;

    const file = files[0];
    try {
      const base64String = await readFileAsBase64(file);
      setSelectedFile({
        name: file.name,
        type: file.type,
        data: base64String,
      });
    } catch (error) {
      console.error('Error reading file:', error);
    }
    // Reset the file input so the same file can be reselected if needed
    event.target.value = null;
  };

  /**
   * Handle keyboard events
   */
  const handleKeyDown = (e) => {
    // Allow sending with enter key
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSendMessage();
      e.preventDefault();
    }
  };

  /**
   * Format timer display
   */
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  /**
   * Toggle audio recording
   */
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorder) {
        mediaRecorder.stop();
        clearInterval(timerRef.current);
        setRecordingTime(0);
      }
      setIsRecording(false);
    } else {
      try {
        // Start recording with optimized settings for Whisper
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 16000, // Whisper works well with 16kHz audio
            echoCancellation: true,
            noiseSuppression: true,
          }
        });

        // Clear previous chunks
        setAudioChunks([]);

        // Determine supported MIME types for browser compatibility
        let mimeType = 'audio/webm';
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
          mimeType = 'audio/ogg;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')) {
          mimeType = 'audio/mp4;codecs=mp4a.40.2';
        }

        setRecordingMimeType(mimeType);

        const recorder = new MediaRecorder(stream, {
          mimeType,
          audioBitsPerSecond: 128000
        });

        // Local array to collect chunks for this recording session
        const currentRecordingChunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            currentRecordingChunks.push(e.data);
            setAudioChunks(prev => [...prev, e.data]);
          }
        };

        recorder.onstop = () => {
          transcribeAudio(stream, currentRecordingChunks);
        };

        // Start recording with data available every 1 second
        recorder.start(1000);
        setMediaRecorder(recorder);
        setIsRecording(true);

        // Start timer
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        showToast("Could not access your microphone. Please check permissions and try again.", "error");
      }
    }
  };

  /**
   * Transcribe audio using Whisper API
   */
  const transcribeAudio = async (stream, specificChunks = null) => {
    try {
      // Release microphone
      stream.getTracks().forEach(track => track.stop());

      // Show loading indicator
      setIsTranscribing(true);
      setTranscriptionStatus('Processing audio');

      const chunksToProcess = specificChunks || audioChunks;
      console.log(`Processing ${chunksToProcess.length} audio chunks`);

      // Create blob with correct MIME type
      const audioBlob = new Blob(chunksToProcess, { type: recordingMimeType });

      // Get file extension based on MIME type
      const getFileExtension = (mimeType) => {
        if (mimeType.includes('webm')) return 'webm';
        if (mimeType.includes('ogg')) return 'ogg';
        if (mimeType.includes('mp4')) return 'mp4';
        return 'webm'; // Default
      };

      const fileExtension = getFileExtension(recordingMimeType);
      console.log(`Using file extension: ${fileExtension}, Blob size: ${(audioBlob.size / 1024).toFixed(2)} KB`);

      // Create FormData for API call
      const formData = new FormData();
      formData.append('file', audioBlob, `recording_${Date.now()}.${fileExtension}`);

      setTranscriptionStatus('Sending to server');

      // Call the Whisper API endpoint
      const response = await authenticatedAxios.post('/api/process_audio', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Expert-ID': user?.sub || '', // Required for Whisper billing
        },
        timeout: 60000, // 60 seconds timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percentCompleted === 100) {
            setTranscriptionStatus('Transcribing with Whisper');
          }
        }
      });

      console.log('Received response from server:', response.data);

      // Validate response
      if (!response.data || !response.data.transcription) {
        throw new Error("No transcription returned from server");
      }

      const transcribedText = response.data.transcription;

      // Check if transcription is empty
      if (!transcribedText.trim()) {
        throw new Error("Couldn't hear anything. Please try again and speak clearly.");
      }

      // Add transcribed text to input field
      setMessage(prev => prev + (prev.length > 0 ? ' ' : '') + transcribedText);

      // Focus input for editing
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error("Error during transcription:", error);
      
      // User-friendly error messages
      let errorMessage = "There was an error transcribing your audio. Please try again.";
      
      if (error.message.includes("hear anything")) {
        errorMessage = "Couldn't detect any speech. Please try again and speak clearly.";
      } else if (error.response && error.response.status === 413) {
        errorMessage = "Recording too long. Please try a shorter message.";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "Transcription timed out. Please try a shorter recording.";
      } else if (error.response && error.response.status === 500) {
        errorMessage = "Server error processing your audio. The team has been notified.";
      }

      showToast(errorMessage, "error");
    } finally {
      setIsTranscribing(false);
      setTranscriptionStatus('');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Hidden file input */}
      <input
        type="file"
        ref={uploadRef}
        onChange={handleFileChange}
        name="files"
        id="file-input"
        style={{ display: 'none' }}
        accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.html,.md,.csv,.json,.epub"
        multiple
      />

      {/* Emoji picker */}
      {emojiPickerVisible && (
        <div
          ref={emojiPickerRef}
          style={{ bottom: '60px', position: 'absolute', left: '1%', zIndex: '100' }}
        >
          <Picker
            data={data}
            onEmojiSelect={emojiSelected}
            theme="dark"
            previewPosition="none"
          />
        </div>
      )}

      {/* Message input container */}
      <Message hasFile={!!selectedFile}>
        <LeftSection>
          {/* Attachment Icon */}
          <IconWrapper onClick={uploadDoc} disabled={loadingMessage}>
            <img
              src={AttachmentIcon}
              alt="attachment icon"
            />
          </IconWrapper>

          {/* Emoji Icon */}
          <IconWrapper onClick={toggleEmojiPicker} disabled={loadingMessage}>
            <img
              src={EmojiIcon}
              alt="emoji icon"
            />
          </IconWrapper>

          {/* Input field for typing messages */}
          <Input
            disabled={loadingMessage}
            ref={inputRef}
            className="conversation-input-ref"
            placeholder="Type a message..."
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
        </LeftSection>

        <RightSection>
          {/* Display selected file info */}
          {selectedFile && (
            <FilePreview>
              <span>{selectedFile.name}</span>
              <RemoveFileButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
              >
                ×
              </RemoveFileButton>
            </FilePreview>
          )}

          {/* Microphone button (shows when no text is typed) */}
          {!message && !selectedFile && (
            <IconWrapper
              style={{ 
                marginRight: '0px',
                position: 'relative'
              }}
              disabled={loadingMessage || isTranscribing}
              onClick={toggleRecording}
            >
              <img
                src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgY2xhc3M9ImZlYXRoZXIgZmVhdGhlci1taWMiPjxwYXRoIGQ9Ik0xMiAyYTQgNCAwIDAgMSA0IDR2NGE0IDQgMCAwIDEtOCAwVjZhNCA0IDAgMCAxIDQtNHoiPjwvcGF0aD48cGF0aCBkPSJNMTkgMTB2MmE3IDcgMCAwIDEtMTQgMHYtMiI+PC9wYXRoPjxsaW5lIHgxPSIxMiIgeTE9IjE5IiB4Mj0iMTIiIHkyPSIyMiI+PC9saW5lPjxsaW5lIHgxPSI4IiB5MT0iMjIiIHgyPSIxNiIgeTI9IjIyIj48L2xpbmU+PC9zdmc+"
                alt="microphone icon"
                style={{
                  width: '20px',
                  height: '20px',
                  filter: isRecording 
                    ? 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)'
                    : 'invert(43%) sepia(57%) saturate(1752%) hue-rotate(190deg) brightness(92%) contrast(98%)'
                }}
              />
              {isRecording && (
                <>
                  <RecordingIndicator />
                  <RecordingTimer>{formatTime(recordingTime)}</RecordingTimer>
                </>
              )}
              {isTranscribing && (
                <TranscriptionLoadingIndicator>
                  {transcriptionStatus || 'Transcribing'}
                </TranscriptionLoadingIndicator>
              )}
            </IconWrapper>
          )}

          {/* Send Icon (shows when there's text or file) */}
          {(message || selectedFile) && (
            <IconWrapper onClick={handleSendMessage} disabled={loadingMessage}>
              <img
                src="https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/send-icon.png"
                alt="send icon"
              />
            </IconWrapper>
          )}
        </RightSection>
      </Message>
    </>
  );
};

export default MessageInput; 